import { Component, computed, effect, input, output, untracked } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { Configuration } from '@ftr/contracts/api/configuration'
import { SearchEntityType, SearchManyResponse } from '@ftr/contracts/api/search'
import { ApiResult, DestroySubscribers, RemoteData, SidePanelSize, TrackingEventType } from '@ftr/foundation'
import { SearchFocusState } from '@ftr/ui-search'
import { SearchResultViewModel } from '~app/features/search/search-results/search-results-list/search-results-list.component'
import { AnalyticsService } from '~app/services/analytics'

export interface SearchManyResultsViewModel {
  searchResults: SearchManyResponse
  configuration: Configuration
  searchFocusState: SearchFocusState
}

export const SEARCH_OVERLAY_SELECTOR = '.search-results-overlay'
@Component({
  selector: 'ftr-global-search-results',
  templateUrl: './global-search-results.component.html',
})
export class GlobalSearchResultsComponent extends DestroySubscribers {
  searchTerm = input.required<string>()
  entitiesToSearch = input.required<SearchEntityType[]>()
  searchResponse = input.required<RemoteData<SearchManyResponse>>()
  configuration = input.required<Configuration>()
  searchFocusState = input.required<SearchFocusState>()

  onNewSearchFocusState = output<SearchFocusState>()
  onItemSelected = output<SearchResultViewModel>()
  onRetry = output()

  viewModel$: ApiResult<SearchManyResultsViewModel> = toObservable(
    computed(() => {
      const searchResultsVal = this.searchResponse()
      const configurationVal = this.configuration()
      const searchFocusState = this.searchFocusState()
      return RemoteData.map2(
        searchResultsVal,
        RemoteData.success(configurationVal),
        (searchResults, configuration) => ({
          searchResults,
          configuration,
          searchFocusState,
        }),
      )
    }),
  )

  readonly sidePanelSize = SidePanelSize

  constructor(private readonly analyticsService: AnalyticsService) {
    super()
    this.trackSearches()
  }

  isEmpty = (data: SearchManyResultsViewModel): boolean => data.searchResults.items.length === 0

  retry(): void {
    this.onRetry.emit()
  }

  private trackSearches(): void {
    effect(() => {
      const searchResponse = this.searchResponse()
      if (searchResponse.isSuccess()) {
        this.analyticsService.track({
          event: TrackingEventType.Search,
          searchTerm: untracked(this.searchTerm),
          eventLabel: `${searchResponse.data!.pagination.totalResults} Results`,
        })
      }
    })
  }
}
